/* eslint-disable prefer-spread */
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { DownloadDocument, DownloadDocumentForPreview } from 'services/DocumentsService';
import IconButton from '@mui/material/IconButton';
import { PREVIEW_TITLE_STYLE } from 'util/Definitions/Constants/Previews';
import { GetSettingBySettingName, UpdateSettingBySettingName } from 'services/UsersService';
import { RegexIsPositiveNumberWith2Decimals } from 'util/RegExUtils';
import DialogContentText from '@mui/material/DialogContentText';
import { DownloadFileResponse } from 'common/models/GenericTypes';
import { DownloadAttachmentFile, DownloadAttachmentFileForPreview } from 'services/MessagesService';
import { PreviewProps } from 'common/models/Previews/Previews';
import {
  DownloadCandidateResume,
  DownloadCandidateResumeForViewer,
} from 'services/CandidatesService';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';

interface Props extends PreviewProps {
  source: 'documents' | 'message-attachments' | 'candidate-resume';
}

const imageExts = ['.jpg', '.jpeg', '.jpe', '.bmp', '.gif', '.png'];
const allowedExts = [
  '.csv',
  '.doc',
  '.docx',
  '.eml',
  '.gif',
  '.htm',
  '.html',
  '.jpeg',
  '.jpg',
  '.odt',
  '.pdf',
  '.png',
  '.rtf',
  '.txt',
  '.xml',
];

const settingName = 'QuickViewDocumentScaleRatio';

export default function DocumentQuickView({
  recordId,
  source,
  isSourceHovered,
  hideCallback,
  hideDelayMs = 1250,
}: Props) {
  const [shouldClose, setShouldClose] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [pdfData, setPdfData] = useState('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfLoadingMessage, setPdfLoadingMessage] = useState<string>();
  const [fileName, setFileName] = useState('');
  const [scale, setScale] = useState(2);
  const [imgSrc, setImgSrc] = useState('');
  const [previewType, setPreviewType] = useState<'pdf' | 'image' | 'other' | null>(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);

  useEffect(() => {
    if (shouldClose) {
      const hidePreviewTimeout = setTimeout(() => {
        hideCallback();
      }, hideDelayMs);
      return () => clearTimeout(hidePreviewTimeout);
    }
  }, [shouldClose, hideDelayMs, hideCallback]);

  useEffect(() => {
    if (isSourceHovered || isHovered || isFetchingFile) setShouldClose(false);
    else setShouldClose(true);
  }, [isSourceHovered, isHovered]);

  useEffect(() => {
    const getDocScaleSetting = async () => {
      const res = await GetSettingBySettingName(settingName);
      if (res && RegexIsPositiveNumberWith2Decimals(res)) setScale(+res);
    };
    getDocScaleSetting();
  }, []);

  const getFileData = useCallback(async () => {
    setIsFetchingData(true);
    let res: DownloadFileResponse | null = null;
    if (source === 'documents') res = await DownloadDocumentForPreview(recordId);
    else if (source === 'message-attachments')
      res = await DownloadAttachmentFileForPreview(recordId);
    else if (source === 'candidate-resume') res = await DownloadCandidateResumeForViewer(recordId);

    if (res) {
      setFileName(res.fileName);

      if (!allowedExts.includes(res.fileExtension)) {
        setPreviewType('other');
      } else if (imageExts.includes(res.fileExtension)) {
        setPreviewType('image');
        const url = URL.createObjectURL(res.data);
        setImgSrc(url);
      } else {
        setPreviewType('pdf');
        const reader = new FileReader();
        reader.readAsDataURL(res.data);
        reader.onloadend = () => {
          const r = reader.result as string;
          setPdfData(r);
        };
      }
    } else setPdfLoadingMessage('Error Loading Preview');

    setIsFetchingData(false);
  }, [recordId, source]);

  useEffect(() => {
    getFileData();
  }, [getFileData]);

  const onScaleChangeHandler = useCallback((value: number) => {
    setScale((prev) => {
      const tmp = prev + value;
      if (tmp <= 0) return prev;
      UpdateSettingBySettingName(settingName, tmp.toFixed(2).toString());
      return tmp;
    });
  }, []);

  const onZoomOut = useCallback(() => {
    onScaleChangeHandler(-0.2);
  }, [onScaleChangeHandler]);

  const onZoomIn = useCallback(() => {
    onScaleChangeHandler(0.2);
  }, [onScaleChangeHandler]);

  const downloadDocumentCallback = useCallback(async () => {
    setIsFetchingFile(true);
    if (source === 'documents') await DownloadDocument(recordId, undefined);
    else if (source === 'message-attachments') await DownloadAttachmentFile(recordId);
    else if (source === 'candidate-resume') await DownloadCandidateResume(recordId);
    setIsFetchingFile(false);
  }, [source, recordId]);

  return (
    <>
      <Backdrop open={isFetchingFile} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress sx={{ color: 'text.disabled' }} />
      </Backdrop>
      <Dialog
        open
        fullWidth
        maxWidth="xl"
        hideBackdrop
        slotProps={{
          paper: {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          },
        }}
      >
        <DialogTitle component="div" display="flex" sx={PREVIEW_TITLE_STYLE}>
          <Box alignSelf="center">
            {source === 'candidate-resume' ? 'Resume' : fileName ? fileName : ''}
          </Box>
          <Box ml="auto">
            {previewType === 'pdf' && (
              <IconButton onClick={onZoomOut} sx={{ color: (t) => t.palette.primary.contrastText }}>
                <ZoomOutIcon />
              </IconButton>
            )}
            {previewType === 'pdf' && (
              <IconButton onClick={onZoomIn} sx={{ color: (t) => t.palette.primary.contrastText }}>
                <ZoomInIcon />
              </IconButton>
            )}
            <IconButton
              onClick={downloadDocumentCallback}
              sx={{ color: (t) => t.palette.primary.contrastText }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={hideCallback}
              sx={{ color: (t) => t.palette.primary.contrastText }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.default', textAlign: 'center' }}>
          {isFetchingData && <CircularProgress />}
          {previewType === 'other' && (
            <DialogContentText component="div" pt={2} px={2}>
              Quick view of this file is not available.{' '}
              <Box
                component="span"
                color="primary.main"
                sx={{ cursor: 'pointer' }}
                onClick={downloadDocumentCallback}
              >
                Click here
              </Box>{' '}
              to download the file instead.
            </DialogContentText>
          )}
          {previewType === 'pdf' && (
            <Document
              file={`data:application/pdf;base64${pdfData}`}
              onLoadSuccess={(p) => setNumPages(p.numPages)}
              loading={pdfLoadingMessage ?? 'Loading...'}
            >
              <Stack spacing={2} pt={2}>
                {Array.apply(null, Array(numPages))
                  .map((x, i) => i + 1)
                  .map((page) => (
                    <Page
                      key={page}
                      pageNumber={page}
                      scale={scale}
                      className="pdf-rendered-page"
                    />
                  ))}
              </Stack>
            </Document>
          )}
          {previewType === 'image' && (
            <img alt="preview" src={imgSrc} style={{ paddingTop: '2rem', maxWidth: '100%' }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
